import { ApolloError, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FormProvider, useForm } from "react-hook-form";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { z } from "zod";
import {
  RESET_PASSWORD,
  SEND_FORGOT_PASSWORD_TOKEN,
} from "../../queries/mutation/login_query";
import FormInput from "../forms/FormInput";

const resetPasswordSchema = z
  .object({
    code: z.string().min(1, "Code must be at least 1 character"),
    newPassword: z
      .string()
      .min(7, "Password must be at least 7 characters long"),
    newPasswordVerify: z
      .string()
      .min(7, "Password must be at least 7 characters long"),
  })
  .refine((data) => data.newPassword === data.newPasswordVerify, {
    message: "Passwords must match",
    path: ["newPasswordVerify"], // Targets the confirmPassword field
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const generateCodeSchema = z.object({
  username: z.string().min(1, "Username must be at least 1 character"),
});

type GenerateCodeFormData = z.infer<typeof generateCodeSchema>;
const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const methods = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onBlur",
  });
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, touchedFields, isSubmitting, isValid },
  } = methods;
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleError = (error: ApolloError) => {
    toast.error(error.message);
  };
  // Request to add credit to discord user account profile
  const [sendForgotPasswordCode] = useMutation(SEND_FORGOT_PASSWORD_TOKEN, {
    onError: handleError,
  });

  const [resetPassword] = useMutation(RESET_PASSWORD, {
    onError: handleError,
  });
  // Send Token Form
  const codeGenerateMethods = useForm<GenerateCodeFormData>({
    resolver: zodResolver(generateCodeSchema),
    mode: "onBlur",
  });
  const {
    handleSubmit: handleCodeSubmit,
    register: registerCode,

    formState: {
      errors: codeErrors,
      touchedFields: codeTouchedfields,
      isSubmitting: isSubmittingCode,
      isValid: isCodeFormValid,
    },
  } = codeGenerateMethods;

  useEffect(() => {
    if (codeSent) {
      methods.reset(); // reset the second form cleanly when it first appears
    } else {
      codeGenerateMethods.reset(); // reset the first form when it disappears
    }
  }, [codeSent]);
  // reset password mutation

  const onSubmitCodeGenerate = async (data: GenerateCodeFormData) => {
    const res = await sendForgotPasswordCode({
      variables: { username: data.username },
    });
    if (res && res.data.sendForgotPasswordToken) {
      setCodeSent(true);
    }
  };
  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      if (!executeRecaptcha) {
        toast("Please complete the CAPTCHA!");
        return;
      }
      const { newPassword, code } = data;
      // The account the code was requested for, from the first form.
      const username = codeGenerateMethods.getValues("username");
      // CHECK CATPCHA
      const recaptchaToken = await executeRecaptcha("reset_password");
      const response = await resetPassword({
        variables: {
          resetPasswordInput: { username, newPassword, token: code, recaptchaToken },
        },
      });
      console.log(response);
      if (response && response.data?.resetPassword) {
        toast.success("Your password has been reset");
        navigate("/Login");
      } else {
        reset({ newPasswordVerify: "" });
      }
    } catch (error) {
      toast.error(`Error: ${error}`);
    }
  };
  return (
    <div className="bg-base-200 h-full flex flex-col justify-center items-center">
      <div className="card bg-base-100 m-2 w-full shadow-lg sm:w-96">
        <div className="card-body">
          <h2 className="card-title ">Forgot Password</h2>
          <div className="divider divider-primary m-0" />
          {codeSent ? (
            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
              >
                <FormInput
                  label="Code"
                  name="code"
                  type="text"
                  register={register("code")}
                  errors={errors.code}
                  touchedFields={touchedFields}
                />
                <FormInput
                  label="New Password"
                  name="newPassword"
                  type="password"
                  register={register("newPassword")}
                  errors={errors.newPassword}
                  touchedFields={touchedFields}
                />
                <FormInput
                  label="Verify New Password"
                  icon={
                    <div
                      onClick={() => setShowPassword(!showPassword)}
                      className={`btn btn-sm btn-ghost ${
                        showPassword ? "text-primary" : ""
                      }`}
                    >
                      {showPassword ? (
                        <FiEye size={20} />
                      ) : (
                        <FiEyeOff size={20} />
                      )}
                    </div>
                  }
                  name="newPasswordVerify"
                  type={showPassword ? "text" : "password"}
                  register={register("newPasswordVerify")}
                  errors={errors.newPasswordVerify}
                  touchedFields={touchedFields}
                />
                <button
                  type="submit"
                  className="btn btn-primary text-white w-full"
                  disabled={isSubmitting || !isValid}
                >
                  {isSubmitting ? "Saving..." : "Reset Password"}
                </button>
                <button
                  className="btn btn-primary text-white w-full"
                  onClick={() => setCodeSent(false)}
                >
                  Send Another Code
                </button>
              </form>
            </FormProvider>
          ) : (
            /* Send code */
            <FormProvider {...codeGenerateMethods}>
              <form
                onSubmit={handleCodeSubmit(onSubmitCodeGenerate)}
                className="flex flex-col gap-4"
              >
                <FormInput
                  label="Username"
                  name="username"
                  type="text"
                  register={registerCode("username")}
                  errors={codeErrors.username}
                  touchedFields={codeTouchedfields}
                />
                <div className="text-sm text-center">
                  A code will be sent to your Discord DM
                </div>

                <button
                  type="submit"
                  className="btn btn-primary text-white w-full"
                  disabled={isSubmittingCode || !isCodeFormValid}
                >
                  {isSubmittingCode
                    ? "Generating Token..."
                    : "Submit Code Request"}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary text-white w-full"
                  onClick={() => setCodeSent(true)}
                >
                  Already have a code?
                </button>
              </form>
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
